<template>
  <div class="border border-[#E5E5E5] rounded-2xl p-6 bg-white shadow-sm">
    <h2 class="font-medium text-xl text-black pb-[1rem] mb-[1rem] border-b border-[#F0F0F0]">
      Informasi Request
    </h2>

    <!-- Partner & Tipe Box -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-[1rem] mb-[1rem]">
      <div class="bg-[#F9F9F9] rounded-xl p-[1rem]">
        <span class="text-[12px] text-[#A3A3A3] tracking-wider uppercase mb-[.5rem] block">PARTNER</span>
        <div class="font-medium text-black text-base mb-[.25rem]">
          {{ partnerName }}
        </div>
        <div class="text-sm text-[#A3A3A3]">
          {{ partnerNo }}
        </div>
      </div>
      <div class="bg-[#F9F9F9] rounded-xl p-[1rem]">
        <span class="text-[12px] text-[#A3A3A3] tracking-wider uppercase mb-[.5rem] block">TIPE REQUEST</span>
        <div class="font-medium text-black text-base mb-[.25rem]">
          {{ requestTypeName }}
        </div>
      </div>
    </div>

    <!-- Talent Card -->
    <div class="border border-[#E5E5E5] rounded-xl p-[1rem] mb-[1rem] bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div class="flex items-center">
        <div
          class="w-12 h-12 rounded-lg me-3 shrink-0 overflow-hidden"
        >
          <img
            :src="talentAvatar"
            :alt="talentName"
            class="w-full h-full object-cover"
          >
        </div>
        <div>
          <div class="font-bold text-black text-base">
            {{ talentName }}
          </div>
          <div
            v-if="talentLeadName"
            class="text-xs text-[#737373] mt-0.5"
          >
            Talent Lead: {{ talentLeadName }}
          </div>
          <div class="text-xs text-[#737373] mt-[.5rem] flex items-center gap-[.5rem] flex-wrap">
            <span v-if="talentCode">{{ talentCode }}</span>
            <span v-if="talentCode && talentWorkDuration">•</span>
            <span
              v-if="talentWorkDuration"
              class="flex items-center gap-[.25rem]"
            >
              <feather-icon
                icon="BriefcaseIcon"
                size="14"
                class="text-[#737373]"
              />
              {{ talentWorkDuration }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="talentSkillName"
        class="sm:self-start"
      >
        <span class="bg-[#D6F1DF] text-[#30A46C] px-[.75rem] py-[.25rem] rounded-lg text-sm inline-block">
          {{ talentSkillName }}
        </span>
      </div>
    </div>

    <!-- Dates Section -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-[1rem] mb-[2rem]">
      <div>
        <span class="text-sm text-[#A3A3A3] mb-[.25rem] block">Tanggal Request</span>
        <span class="font-semibold text-black text-sm">{{ formattedRequestDate }}</span>
      </div>
      <div>
        <span class="text-sm text-[#A3A3A3] mb-[.25rem] block">Estimasi Efektif (TL)</span>
        <span class="font-semibold text-black text-sm">{{ formattedEstimatedDate }}</span>
      </div>
      <div>
        <div v-if="hasFinalEffectiveDate">
          <span class="text-sm text-[#A3A3A3] mb-[.25rem] block">Tanggal Efektif Final</span>
          <span
            class="font-semibold text-black text-sm"
          >
            {{ formattedFinalDate }}
          </span>
        </div>
        <div
          v-else
          ref="popoverContainer"
          class="relative inline-block"
        >
          <BButton
            variant="outline-primary"
            class="border-[#EC5A24] text-[#EC5A24] bg-white hover:bg-[#FFF2EE] rounded-xl px-[.75rem] py-[.25rem] text-sm font-medium"
            @click.stop="toggleDatePopover"
          >
            Perpanjang
          </BButton>

          <!-- Custom Self-Made Popover -->
          <div
            v-if="showDatePopover"
            class="custom-date-popover absolute left-0 top-[calc(100%+8px)] z-50 bg-white rounded-2xl p-[.75rem] shadow-xl border border-[#E5E5E5] w-[290px]"
            @click.stop
          >
            <b-calendar
              v-model="selectedEffectiveDate"
              locale="id"
              class="w-full border-0 shadow-none"
              :min="minEffectiveDate"
            />
            <div class="flex items-center justify-end gap-[.75rem] mt-[.75rem] pt-[.5rem] border-t border-[#E5E5E5]">
              <BButton
                variant="link"
                @click="closeDatePopover"
              >
                Batal
              </BButton>
              <BButton
                variant="primary"
                :disabled="!selectedEffectiveDate || isLoadingSubmit"
                @click="submitEffectiveDate"
              >
                <BSpinner
                  v-if="isLoadingSubmit"
                  small
                />
                <span v-else>Terapkan</span>
              </BButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alasan Churn -->
    <div class="mb-[1.5rem]">
      <span class="font-semibold text-black text-sm mb-[.5rem] block">Alasan Churn</span>
      <div class="bg-[#F5F5F5] rounded-xl p-3.5 border border-[#E5E5E5] text-black text-sm min-h-[75px]">
        {{ reasonName }}
      </div>
    </div>

    <!-- Keterangan Tambahan -->
    <div>
      <span class="font-semibold text-black text-sm mb-[.5rem] block">Keterangan Tambahan</span>
      <div class="bg-[#F5F5F5] rounded-xl p-3.5 border border-[#E5E5E5] text-black text-sm min-h-[75px]">
        {{ additionalNotes }}
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { DDMMYYYY, YEAR_MONTH_DAY } from '@/libs/filterDate'
import { komtimAxiosIns } from '@/libs/axios'
import { showSuccess, showError } from '../../config'

export default {
  name: 'RequestInfoCard',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showDatePopover: false,
      selectedEffectiveDate: '',
      isLoadingSubmit: false,
    }
  },
  computed: {
    partnerName() {
      return (this.data && this.data.partner && this.data.partner.name) || '-'
    },
    partnerNo() {
      return (this.data && this.data.partner && this.data.partner.no_partner) || '-'
    },
    requestTypeName() {
      return (this.data && this.data.request_type && this.data.request_type.name) || '-'
    },
    talentAvatar() {
      return (this.data && this.data.talent && this.data.talent.avatar_url) || 'https://storage.googleapis.com/komerce/assets/avatars/image-null.png'
    },
    talentName() {
      return (this.data && this.data.talent && this.data.talent.name) || '-'
    },
    talentLeadName() {
      return (this.data && this.data.talent && this.data.talent.talent_lead_name) || ''
    },
    talentCode() {
      return (this.data && this.data.talent && this.data.talent.id) || ''
    },
    talentWorkDuration() {
      return (this.data && this.data.talent && this.data.talent.work_duration) || ''
    },
    talentSkillName() {
      return (this.data && this.data.talent && this.data.talent.skill_name) || ''
    },
    formattedRequestDate() {
      return this.formatDate(this.data && this.data.request_date)
    },
    formattedEstimatedDate() {
      return this.formatDate(this.data && this.data.estimated_effective_date)
    },
    formattedFinalDate() {
      return this.formatDate(this.data && this.data.final_effective_date)
    },
    minEffectiveDate() {
      if (!this.data || !this.data.estimated_effective_date) return null
      const date = moment(this.data.estimated_effective_date)
      return date.isValid() ? date.add(1, 'days').format('YYYY-MM-DD') : null
    },
    hasFinalEffectiveDate() {
      return Boolean(this.data && this.data.final_effective_date)
    },
    reasonName() {
      return (this.data && this.data.reason_name) || ''
    },
    additionalNotes() {
      return (this.data && this.data.additional_notes) || ''
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    formatDate(val) {
      if (!val) return '-'
      return DDMMYYYY(val)
    },
    toggleDatePopover() {
      this.showDatePopover = !this.showDatePopover
    },
    closeDatePopover() {
      this.showDatePopover = false
    },
    handleOutsideClick(event) {
      if (!this.showDatePopover) return
      const container = this.$refs.popoverContainer
      if (container && !container.contains(event.target)) {
        this.closeDatePopover()
      }
    },
    async submitEffectiveDate() {
      if (!this.selectedEffectiveDate) return
      const requestId = (this.data && this.data.id) || this.$route.params.id
      if (!requestId) return

      try {
        this.isLoadingSubmit = true
        const formattedDate = YEAR_MONTH_DAY(this.selectedEffectiveDate)
        const url = `/v1/partner_assignment_requests/${requestId}/effective_date`
        await komtimAxiosIns.put(url, {
          final_effective_date: formattedDate,
        })
        showSuccess({ title: 'Berhasil mengubah tanggal efektif final!' })
        this.closeDatePopover()
        this.$emit('updated')
      } catch (err) {
        showError({
          title: 'Gagal mengubah tanggal efektif final!',
        })
      } finally {
        this.isLoadingSubmit = false
      }
    },
  },
}
</script>

<style scoped>
::v-deep .extend-date-popover {
  border-radius: 1rem;
  border: 1px solid #E5E5E5;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
}
::v-deep .extend-date-popover .popover-body {
  padding: 0;
}
::v-deep .b-calendar .btn-primary {
  background-color: #EC5A24 !important;
  border-color: #EC5A24 !important;
}
.btn-primary, .btn-link {
  border-radius: 10px !important;
}
</style>
